import { X } from "lucide-react";
import { useState } from "react";
import { PortalContainerContext } from "@/components/PortalContainer";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";

interface FilterSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onApply: () => void;
  onCancel: () => void;
  onClear: () => void;
  hasFilters: boolean;
  children: React.ReactNode;
}

export function FilterSheet({
  isOpen,
  onOpenChange,
  onApply,
  onCancel,
  onClear,
  hasFilters,
  children,
}: FilterSheetProps) {
  const [container, setContainer] = useState<HTMLDivElement | null>(null);

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent ref={setContainer} className="flex w-full flex-col gap-0 p-0">
        <SheetHeader className="p-6 pb-4 pr-12">
          <SheetTitle>Filters</SheetTitle>
          {/* Kept for screen readers when hidden: it is the sheet's accessible description. */}
          <SheetDescription className="short:sr-only">
            Apply filters to narrow down your results. Click apply when ready.
          </SheetDescription>
        </SheetHeader>
        {/* Only the filters scroll, so the actions stay pinned to the bottom however many filters there are. */}
        <div data-testid="filter-sheet-body" className="flex-1 overflow-y-auto px-6 pb-6">
          <PortalContainerContext.Provider value={container ?? undefined}>
            <div className="space-y-4">{children}</div>
          </PortalContainerContext.Provider>
        </div>
        <div
          data-testid="filter-sheet-footer"
          className="flex shrink-0 gap-2 border-t border-border bg-background px-6 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] compact:grid compact:grid-cols-2"
        >
          <Button variant="outline" onClick={onClear} disabled={!hasFilters}>
            <X className="h-4 w-4 mr-2" />
            Clear Filters
          </Button>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="confirm" onClick={onApply} className="col-span-2">
            Apply Filters
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
